import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { Link, useNavigate, useLocation, useNavigationType } from 'react-router-dom';
import { Search, Package, MapPin, ArrowRight, Star, Users, ShoppingBag, ArrowLeftRight, Navigation, Sparkles, TrendingUp, ChevronLeft, ChevronRight, Rocket, BadgeCheck, Heart, Gavel, Store, Tag, Eye, Wrench, Briefcase, Calendar, Clock, Globe, CheckCircle, Wallet } from 'lucide-react';
import axios from '../lib/axios';
import { API, useLanguage, useCurrency, useIsland, useAuth, LOGO_URL_DARK, LOGO_URL_LIGHT, HERO_ICON_URL } from '../App';
import { useTheme } from '../App';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ListingCard from '../components/ListingCard';
import NewsTicker from '../components/NewsTicker';
import { LargePromoSection, SmallPromosSection, DoubleBannerSection } from '../components/PromoBlock';
import { getIconComponent, getIconEmoji } from '../utils/categoryIcons';

// Hero background images (Cloudinary optimized - auto format WebP, auto quality)
const HERO_IMAGE_LIGHT = 'https://res.cloudinary.com/dtqazyw6v/image/upload/f_auto,q_auto,w_1920/yourbuysitehero/hero_light_willemstad';
const HERO_IMAGE_DARK = 'https://res.cloudinary.com/dtqazyw6v/image/upload/f_auto,q_auto,w_1920/yourbuysitehero/hero_dark_willemstad';

// Dynamic Hero Text Matrix - Island & Language Specific
const ISLAND_HERO_TEXT = {
  aruba: {
    en: {
      headline: "The Caribbean's First 24/7 Digital Marketplace",
      description: ''
    },
    nl: {
      headline: '#1 Marktplaats van de Eilanden',
      description: ''
    },
    es: {
      headline: '#1 Mercado de las Islas',
      description: ''
    }
  },
  curacao: {
    en: {
      headline: "The Caribbean's First 24/7 Digital Marketplace",
      description: ''
    },
    nl: {
      headline: '#1 Marktplaats van de Eilanden',
      description: ''
    },
    es: {
      headline: '#1 Mercado de las Islas',
      description: ''
    }
  },
  bonaire: {
    en: {
      headline: "The Caribbean's First 24/7 Digital Marketplace",
      description: ''
    },
    nl: {
      headline: '#1 Marktplaats van de Eilanden',
      description: ''
    },
    es: {
      headline: '#1 Mercado de las Islas',
      description: ''
    }
  },
  // Fallback for "all" islands or unknown
  all: {
    en: {
      headline: "The Caribbean's First 24/7 Digital Marketplace",
      description: ''
    },
    nl: {
      headline: '#1 Marktplaats van de Eilanden',
      description: ''
    },
    es: {
      headline: '#1 Mercado de las Islas',
      description: ''
    }
  }
};

// Hero Action Translations
const HERO_ACTION_TEXT = {
  en: {
    badge: "Islands Favourite",
    headline_1: "Buy, Sell & Trade.",
    headline_2: "",
    headline_3: "The first digital Caribbean Marketplace",
    subtitle: "",
    cta: "Sell Now Free",
    forever: "Forever",
    no_fees: "No commission • No subscription"
  },
  nl: {
    badge: "Islands Favourite",
    headline_1: "Koop, Verkoop & Ruil.",
    headline_2: "",
    headline_3: "De eerste digitale Caribische Marktplaats",
    subtitle: "",
    cta: "Verkoop Nu Gratis",
    forever: "Voor altijd",
    no_fees: "Geen commissie • Geen abonnement"
  },
  es: {
    badge: "Favorito de las Islas",
    headline_1: "Compra, Vende e Intercambia.",
    headline_2: "",
    headline_3: "El primer Mercado Digital del Caribe",
    subtitle: "",
    cta: "Vende Gratis Ahora",
    forever: "Para siempre",
    no_fees: "Sin comisión • Sin suscripción"
  }
};

// Get hero action text based on language
const getHeroActionText = (language) => {
  const langKey = language?.toLowerCase() || 'en';
  return HERO_ACTION_TEXT[langKey] || HERO_ACTION_TEXT.en;
};

// Get currency symbol for hero display
const getHeroCurrencyDisplay = (currency) => {
  switch (currency) {
    case 'XCG':
      return 'XCG 0,00';
    case 'USD':
      return '$ 0,00';
    case 'both':
    case 'EUR':
    default:
      return '$ 0,00';
  }
};

// Island options for hero selection
const ISLAND_OPTIONS = [
  { code: 'aruba', name: 'Aruba', flag: '🇦🇼' },
  { code: 'bonaire', name: 'Bonaire', flag: '🇧🇶' },
  { code: 'curacao', name: 'Curaçao', flag: '🇨🇼' },
];

// Get hero text based on island and language
const getHeroText = (island, language) => {
  const islandKey = island?.toLowerCase() || 'curacao';
  const langKey = language?.toLowerCase() || 'en';
  
  // Get island-specific text or fallback to 'curacao'
  const islandTexts = ISLAND_HERO_TEXT[islandKey] || ISLAND_HERO_TEXT.curacao;
  // Get language-specific text or fallback to 'en'
  return islandTexts[langKey] || islandTexts.en;
};

// Top Ads Carousel Component
const TopAdsCarousel = ({ listings, formatPrice, formatDualPrice, t }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const scrollContainerRef = useRef(null);

  // Category-specific placeholder images
  const CATEGORY_IMAGES = {
    'real-estate': 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=800&h=500&fit=crop',
    'vehicles': 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=800&h=500&fit=crop',
    'electronics': 'https://images.unsplash.com/photo-1498049794561-7780e7231661?w=800&h=500&fit=crop',
    'fashion': 'https://images.unsplash.com/photo-1445205170230-053b83016050?w=800&h=500&fit=crop',
    'furniture': 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=800&h=500&fit=crop',
    'services': 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=800&h=500&fit=crop',
    'jobs': 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=500&fit=crop',
    'other': 'https://images.unsplash.com/photo-1472851294608-062f824d29cc?w=800&h=500&fit=crop',
  };

  const getDefaultImage = (category) => CATEGORY_IMAGES[category] || CATEGORY_IMAGES['other'];

  const getTypeLabel = (type) => {
    const types = { sale: t('for_sale'), rent: t('for_rent'), trade: t('for_trade'), service: t('services'), auction: 'Auction', yard_sale: 'Yard Sale' };
    return types[type] || t('for_sale');
  };

  const getTypeBadgeClass = (type) => {
    const classes = { 
      sale: 'bg-blue-600 text-white', 
      rent: 'bg-purple-600 text-white', 
      trade: 'bg-teal-600 text-white', 
      service: 'bg-indigo-600 text-white',
      auction: 'bg-rose-600 text-white',
      yard_sale: 'bg-amber-600 text-white'
    };
    return classes[type] || 'bg-blue-600 text-white';
  };

  // Auto-scroll effect
  useEffect(() => {
    if (!isAutoPlaying || listings.length <= 4) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % listings.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, listings.length]);

  // Scroll to current index
  useEffect(() => {
    if (scrollContainerRef.current && listings.length > 0) {
      const cardWidth = 320; // Approximate card width + gap
      scrollContainerRef.current.scrollTo({
        left: currentIndex * cardWidth,
        behavior: 'smooth'
      });
    }
  }, [currentIndex, listings.length]);

  const scrollPrev = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev - 1 + listings.length) % listings.length);
  };

  const scrollNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % listings.length);
  };

  if (listings.length === 0) return null;

  return (
    <section className="py-12 md:py-16 bg-gradient-to-b from-background to-secondary/20" data-testid="top-ads-carousel">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <span className="inline-flex items-center gap-2 text-amber-500 font-semibold text-sm mb-2 uppercase tracking-wider">
              <Rocket className="w-4 h-4" />
              Top Promoted Ads
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight">
              Featured Deals
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={scrollPrev}
              className="w-10 h-10 rounded-full bg-card border border-border text-foreground flex items-center justify-center hover:bg-primary hover:text-white hover:border-primary transition-all duration-200 shadow-sm"
              data-testid="carousel-prev-btn"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={scrollNext}
              className="w-10 h-10 rounded-full bg-card border border-border text-foreground flex items-center justify-center hover:bg-primary hover:text-white hover:border-primary transition-all duration-200 shadow-sm"
              data-testid="carousel-next-btn"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <Link 
              to="/marketplace?promoted=true"
              className="hidden md:inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full font-medium hover:bg-primary/20 transition-colors"
            >
              {t('view_all')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* Carousel Container */}
        <div 
          ref={scrollContainerRef}
          className="flex gap-5 overflow-x-auto pb-4 scrollbar-hide scroll-smooth"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          onMouseEnter={() => setIsAutoPlaying(false)}
          onMouseLeave={() => setIsAutoPlaying(true)}
        >
          {listings.map((listing, index) => {
            const defaultImage = getDefaultImage(listing.category);
            const image = listing.images?.[0] || defaultImage;
            const pricing = listing.pricing || { amount: listing.price || 0, currency: 'USD' };
            // Show island name only on listing cards (not specific location)
            const ISLAND_NAMES = { aruba: 'Aruba', curacao: 'Curaçao', bonaire: 'Bonaire' };
            const displayLocation = listing.visible_islands?.length >= 1 
              ? ISLAND_NAMES[listing.visible_islands[0]] || listing.visible_islands[0]
              : (listing.location?.country || 'Caribbean');

            return (
              <Link
                key={listing.id}
                to={`/listing/${listing.slug || listing.id}`}
                className="flex-none w-[280px] md:w-[300px] group"
                data-testid={`featured-card-${index}`}
              >
                {/* Card - matches ListingCard style */}
                <div className="bg-card rounded-2xl overflow-hidden border border-border hover:border-primary/50 hover:shadow-xl transition-all duration-300 h-full">
                  {/* Image Container */}
                  <div className="relative aspect-[4/3] overflow-hidden bg-muted">
                    <img
                      src={image}
                      alt={listing.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-"
                      onError={(e) => { e.target.src = defaultImage; }}
                    />
                    
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
                    
                    {/* Top badges */}
                    <div className="absolute top-3 left-3 flex items-center gap-2">
                      <span className={`px-2.5 py-1 text-xs font-semibold rounded-full shadow-md ${getTypeBadgeClass(listing.listing_type)}`}>
                        {getTypeLabel(listing.listing_type)}
                      </span>
                      {listing.is_promoted && (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md flex items-center gap-1">
                          <Star className="w-3 h-3 fill-white" />
                          Featured
                        </span>
                      )}
                    </div>

                    {/* Favorite button */}
                    <div className="absolute top-3 right-3">
                      <button 
                        className="w-8 h-8 bg-white/90 dark:bg-slate-800/90 rounded-full flex items-center justify-center shadow-md hover:bg-white dark:hover:bg-slate-800 transition-all "
                        onClick={(e) => e.preventDefault()}
                      >
                        <Heart className="w-4 h-4 text-slate-400 hover:text-red-500 transition-colors" />
                      </button>
                    </div>

                    {/* Verified badge */}
                    {listing.is_verified_business && (
                      <div className="absolute bottom-3 left-3">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-600 text-white text-xs font-medium rounded-full shadow-md">
                          <BadgeCheck className="w-3 h-3" />
                          Verified
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    {/* Category */}
                    <div className="flex items-center gap-1.5 mb-2">
                      <span className="text-xs text-muted-foreground capitalize">{listing.category?.replace('-', ' ')}</span>
                    </div>

                    {/* Title */}
                    <h3 className="font-semibold text-foreground line-clamp-2 mb-2 group-hover:text-primary transition-colors">
                      {listing.title}
                    </h3>

                    {/* Price */}
                    <div className="mb-3">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-xl font-bold text-primary">
                          {formatDualPrice(pricing.amount, pricing.currency).usd}
                        </span>
                        <span className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">
                          {formatDualPrice(pricing.amount, pricing.currency).xcg}
                        </span>
                      </div>
                      {listing.listing_type === 'rent' && (
                        <span className="text-xs text-muted-foreground">/month</span>
                      )}
                    </div>

                    {/* Location & Views */}
                    <div className="flex items-center justify-between text-sm text-muted-foreground border-t border-border pt-3">
                      <div className="flex items-center gap-1.5 truncate">
                        <MapPin className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">{displayLocation}</span>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                        <Eye className="w-4 h-4" />
                        <span>{listing.views || 0}</span>
                      </div>
                    </div>

                    {/* Seller */}
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
                      <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium text-primary overflow-hidden">
                        {listing.user_avatar ? (
                          <img src={listing.user_avatar} alt={listing.user_name} className="w-full h-full object-cover" />
                        ) : (
                          listing.user_name?.charAt(0).toUpperCase()
                        )}
                      </div>
                      <span className="text-sm text-foreground truncate flex-1">{listing.user_name}</span>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}

          {/* View All Card */}
          <Link
            to="/marketplace"
            className="flex-none w-[280px] md:w-[300px] group"
            data-testid="view-all-card"
          >
            <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl border-2 border-dashed border-primary/30 hover:border-primary/50 transition-all duration-300 h-full flex flex-col items-center justify-center p-8 text-center hover:bg-primary/10">
              <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mb-4 group- transition-transform">
                <ArrowRight className="w-8 h-8 text-primary" />
              </div>
              <span className="font-bold text-lg text-foreground mb-2">{t('view_all_listings')}</span>
              <span className="text-sm text-muted-foreground">{t('browse_promoted_deals').replace('{count}', listings.length)}</span>
            </div>
          </Link>
        </div>

        {/* Dots Indicator */}
        <div className="flex items-center justify-center gap-2 mt-6">
          {listings.slice(0, Math.min(listings.length, 8)).map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setIsAutoPlaying(false);
                setCurrentIndex(index);
              }}
              className={`transition-all duration-300 rounded-full ${
                index === currentIndex % Math.min(listings.length, 8)
                  ? 'w-8 h-2 bg-primary' 
                  : 'w-2 h-2 bg-muted-foreground/30 hover:bg-muted-foreground/50'
              }`}
              aria-label={`Go to slide ${index + 1}`}
              data-testid={`carousel-dot-${index}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const HomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useLanguage();
  const { theme } = useTheme();
  const { currency, formatPrice, formatDualPrice, USD_TO_XCG } = useCurrency();
  const { currentIsland, setCurrentIsland, getCurrentIslandData } = useIsland();
  const { user } = useAuth();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState([]);
  const [featuredListings, setFeaturedListings] = useState([]);
  const [recentListings, setRecentListings] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [specialCounts, setSpecialCounts] = useState({ auctions: 0, bulk: 0, yard_sales: 0, giveaways: 0 });
  const scrollRestoredRef = useRef(false);
  const navigationType = useNavigationType(); // 'POP' | 'PUSH' | 'REPLACE'

  // Scroll restoration for back navigation
  useLayoutEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // Restore scroll position when coming back to homepage via back navigation
  useEffect(() => {
    // Only restore scroll on back navigation (POP)
    if (navigationType !== 'POP') {
      scrollRestoredRef.current = false;
      return;
    }
    
    const savedScrollY = sessionStorage.getItem('homeScrollY');
    if (savedScrollY && parseInt(savedScrollY) > 0 && !scrollRestoredRef.current) {
      scrollRestoredRef.current = true;
      
      // Wait for content to be ready, then restore scroll
      const restoreScroll = () => {
        const targetY = parseInt(savedScrollY);
        window.scrollTo({ top: targetY, behavior: 'instant' });
        
        // Verify scroll was applied, retry if needed (content might still be loading)
        requestAnimationFrame(() => {
          if (Math.abs(window.scrollY - targetY) > 100) {
            window.scrollTo({ top: targetY, behavior: 'instant' });
          }
          sessionStorage.removeItem('homeScrollY');
        });
      };
      
      // Small delay to ensure React has rendered
      setTimeout(restoreScroll, 50);
    }
  }, [navigationType]);

  // Also restore when loading completes (for back navigation with slow content)
  useEffect(() => {
    if (!loading && navigationType === 'POP' && !scrollRestoredRef.current) {
      const savedScrollY = sessionStorage.getItem('homeScrollY');
      if (savedScrollY && parseInt(savedScrollY) > 0) {
        scrollRestoredRef.current = true;
        const targetY = parseInt(savedScrollY);
        
        // Content is loaded, restore scroll
        setTimeout(() => {
          window.scrollTo({ top: targetY, behavior: 'instant' });
          sessionStorage.removeItem('homeScrollY');
        }, 100);
      }
    }
  }, [loading, navigationType]);

  // Save scroll position when clicking on a listing
  useEffect(() => {
    const handleClick = (e) => {
      const link = e.target.closest('a[href^="/listing/"]');
      if (link) {
        sessionStorage.setItem('homeScrollY', window.scrollY.toString());
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  // Get island query param
  const islandParam = currentIsland ? `&island=${currentIsland}` : '';

  useEffect(() => {
    fetchData();
    fetchSpecialCounts();
  }, [language, currentIsland]);

  const fetchSpecialCounts = async () => {
    try {
      const [auctionRes, bulkRes, yardRes, giveawayRes] = await Promise.all([
        axios.get(`${API}/listings?listing_type=auction&limit=1${islandParam}`),
        axios.get(`${API}/listings?listing_type=bulk&limit=1${islandParam}`),
        axios.get(`${API}/listings?listing_type=yard_sale&limit=1${islandParam}`),
        axios.get(`${API}/listings?listing_type=giveaway&limit=1${islandParam}`),
      ]);
      setSpecialCounts({
        auctions: auctionRes.data.total || 0,
        bulk: bulkRes.data.total || 0,
        yard_sales: yardRes.data.total || 0,
        giveaways: giveawayRes.data.total || 0,
      });
    } catch (error) {
      console.error('Error fetching special counts:', error);
    }
  };

  const fetchData = async () => {
    try {
      const islandQuery = currentIsland ? `?island=${currentIsland}` : '';
      const [catRes, featuredRes, recentRes, eventsRes] = await Promise.all([
        axios.get(`${API}/categories?lang=${language}`),
        axios.get(`${API}/listings/featured${islandQuery}`),
        axios.get(`${API}/listings/recent${islandQuery}`),
        axios.get(`${API}/events?status=approved&limit=6`),
      ]);
      setCategories(catRes.data);
      // API returns { listings: [...] } - extract the array
      setFeaturedListings(featuredRes.data?.listings || featuredRes.data || []);
      setRecentListings(recentRes.data?.listings || recentRes.data || []);
      // Events are already filtered to future by the backend (include_past=false by default)
      setUpcomingEvents(eventsRes.data?.events || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/marketplace?search=${encodeURIComponent(searchQuery)}`);
    } else {
      navigate('/marketplace');
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="home-page">
      <Navbar />
      
      {/* Hero Section - Bold V4 Split Design */}
      <section 
        className="hero-section relative overflow-hidden min-h-[420px] md:min-h-[480px]"
        data-testid="hero-section"
      >
        {/* Light Mode - Willemstad Day Scene */}
        <div className="absolute inset-0 light-hero-bg dark:hidden">
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ 
              backgroundImage: `url('${HERO_IMAGE_LIGHT}')`,
            }}
          />
          {/* Darker overlay for better text contrast */}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/70 via-slate-900/50 to-slate-900/30" />
          {/* Smooth gradient fade to website background - Light Mode (smaller, less intense) */}
          <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background via-background/60 to-transparent" />
        </div>
        
        {/* Dark Mode - Willemstad Night Scene */}
        <div className="absolute inset-0 hidden dark:block">
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ 
              backgroundImage: `url('${HERO_IMAGE_DARK}')`,
            }}
          />
          {/* Gradient overlay for text contrast */}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/70 via-slate-900/50 to-slate-900/30" />
          {/* Smooth gradient fade to website background - Dark Mode */}
          <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-background via-background/90 to-transparent" />
        </div>
        
        {/* Content */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12 relative z-10">
          
          {/* Mobile Logo */}
          <div className="md:hidden text-center mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_island-marketplace-3/artifacts/1uy6w34w_logoparadiseNOBACK.png" 
              alt="YourBuySite" 
              className="w-72 h-auto mx-auto drop-shadow-2xl"
            />
          </div>
          
          {/* ===== MOBILE LAYOUT ===== */}
          <div className="md:hidden text-center">
            {/* Stats Row - 3 boxes */}
            <div className="flex justify-center gap-2 mb-5">
              <div className="bg-white/10 backdrop-blur-md rounded-xl px-3 py-2 border border-white/20">
                <Globe className="w-5 h-5 text-primary mx-auto mb-1" />
                <p className="text-white font-bold text-sm">{language === 'nl' ? '3 Eilanden' : language === 'es' ? '3 Islas' : '3 Islands'}</p>
                <p className="text-white/50 text-[10px]">{language === 'nl' ? 'Verbonden' : language === 'es' ? 'Conectadas' : 'Connected'}</p>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-xl px-3 py-2 border border-white/20">
                <Clock className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
                <p className="text-white font-bold text-sm">60 Sec</p>
                <p className="text-white/50 text-[10px]">{language === 'nl' ? 'Maak & Plaats' : language === 'es' ? 'Crear y Publicar' : 'Create & List'}</p>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-xl px-3 py-2 border border-white/20">
                <CheckCircle className="w-5 h-5 text-amber-400 mx-auto mb-1" />
                <p className="text-white font-bold text-sm">100%</p>
                <p className="text-white/50 text-[10px]">{language === 'nl' ? 'Gratis' : language === 'es' ? 'Gratis' : 'Free'}</p>
              </div>
            </div>
            
            {/* Two CTA Buttons Stacked */}
            <div className="space-y-3">
              <Link 
                to={user ? "/create-listing" : "/register"}
                className="flex items-center justify-center gap-2 bg-gradient-to-r from-primary to-cyan-500 text-white px-6 py-3.5 rounded-xl font-bold text-base shadow-xl w-full group"
                data-testid="hero-cta-sell-mobile"
              >
                <Rocket className="w-5 h-5 animate-pulse" />
                <span className="animate-pulse">
                  {language === 'nl' ? 'Start Nu!' : language === 'es' ? '¡Empieza Ahora!' : 'Start Now!'}
                </span>
              </Link>
              <Link 
                to="/marketplace"
                className="flex items-center justify-center gap-2 bg-white hover:bg-white/90 text-slate-900 px-6 py-3.5 rounded-xl font-bold text-base w-full shadow-xl"
                data-testid="hero-cta-browse-mobile"
              >
                <Search className="w-5 h-5" />
                {t('browse_listings')}
              </Link>
              
              {/* USDT Payment Strip - Mobile */}
              <div className="flex items-center justify-center gap-2 px-4 py-2.5 bg-[#26A17B]/10 border border-[#26A17B]/30 rounded-xl" data-testid="usdt-strip-mobile">
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-teal-500 to-emerald-500 text-white text-[10px] font-bold rounded-full shadow-lg ring-2 ring-white/30">
                  <Wallet className="w-3 h-3" />
                  USDT
                </span>
                <span className="text-white/90 text-xs">
                  {language === 'nl' ? (
                    <>badge op een listing? Verkoper accepteert <strong className="text-[#26A17B]">USDT</strong>!</>
                  ) : language === 'es' ? (
                    <>en un anuncio? ¡El vendedor acepta <strong className="text-[#26A17B]">USDT</strong>!</>
                  ) : (
                    <>badge on a listing? Seller accepts <strong className="text-[#26A17B]">USDT</strong>!</>
                  )}
                </span>
              </div>
            </div>
          </div>
          
          {/* ===== DESKTOP LAYOUT D (Volledig Gecentreerd) ===== */}
          <div className="hidden md:block text-center">
            {/* YBS Logo */}
            <img 
              src="https://customer-assets.emergentagent.com/job_island-marketplace-3/artifacts/1uy6w34w_logoparadiseNOBACK.png" 
              alt="Your Buy Site" 
              className="h-44 lg:h-48 w-auto mx-auto mb-6 drop-shadow-2xl"
            />
            
            {/* Stats Row - 3 boxes */}
            <div className="flex justify-center gap-6 mb-8">
              <div className="bg-white/10 backdrop-blur-md rounded-xl px-6 py-4 border border-white/20">
                <Globe className="w-6 h-6 text-primary mx-auto mb-2" />
                <p className="text-white font-bold">{language === 'nl' ? '3 Eilanden' : language === 'es' ? '3 Islas' : '3 Islands'}</p>
                <p className="text-white/50 text-xs">{language === 'nl' ? 'Verbonden' : language === 'es' ? 'Conectadas' : 'Connected'}</p>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-xl px-6 py-4 border border-white/20">
                <Clock className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                <p className="text-white font-bold">60 Sec</p>
                <p className="text-white/50 text-xs">{language === 'nl' ? 'Maak & Plaats' : language === 'es' ? 'Crear y Publicar' : 'Create & List'}</p>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-xl px-6 py-4 border border-white/20">
                <CheckCircle className="w-6 h-6 text-amber-400 mx-auto mb-2" />
                <p className="text-white font-bold">100%</p>
                <p className="text-white/50 text-xs">{language === 'nl' ? 'Gratis Voor Altijd' : language === 'es' ? 'Gratis Para Siempre' : 'Free Forever'}</p>
              </div>
            </div>
            
            {/* CTA Buttons and USDT Strip Container */}
            <div className="inline-flex flex-col items-center">
              {/* Two CTA Buttons Side by Side */}
              <div className="flex justify-center gap-4">
                <Link 
                  to={user ? "/create-listing" : "/register"}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-primary to-cyan-500 hover:from-primary/90 hover:to-cyan-500/90 text-white px-10 py-4 rounded-xl font-bold text-lg shadow-xl transition-all hover:scale-105 group"
                  data-testid="hero-cta-sell-desktop"
                >
                  <Rocket className="w-5 h-5 animate-pulse" />
                  <span className="animate-pulse">
                    {language === 'nl' ? 'Start Nu!' : language === 'es' ? '¡Empieza Ahora!' : 'Start Now!'}
                  </span>
                </Link>
                <Link 
                  to="/marketplace"
                  className="inline-flex items-center gap-2 bg-white hover:bg-white/90 text-slate-900 px-10 py-4 rounded-xl font-bold text-lg transition-all hover:scale-105 shadow-xl"
                  data-testid="hero-cta-browse-desktop"
                >
                  <Search className="w-5 h-5" />
                  {t('browse_listings')}
                </Link>
              </div>
              
              {/* USDT Payment Strip - same width as buttons */}
              <div className="flex items-center justify-center gap-3 mt-4 px-5 py-3 bg-[#26A17B]/10 border border-[#26A17B]/30 rounded-xl backdrop-blur-sm w-full" data-testid="usdt-strip-desktop">
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-teal-500 to-emerald-500 text-white text-xs font-bold rounded-full shadow-lg ring-2 ring-white/30">
                  <Wallet className="w-3.5 h-3.5" />
                  USDT
                </span>
                <span className="text-white/90 text-sm">
                  {language === 'nl' ? (
                    <>badge op een listing? Verkoper accepteert <strong className="text-[#26A17B]">USDT</strong>!</>
                  ) : language === 'es' ? (
                    <>en un anuncio? ¡El vendedor acepta <strong className="text-[#26A17B]">USDT</strong>!</>
                  ) : (
                    <>badge on a listing? Seller accepts <strong className="text-[#26A17B]">USDT</strong>!</>
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* News Ticker - Below Hero */}
      <NewsTicker showOn="homepage" />

      {/* ========== SECTION 1: Categories Carousel (Desktop + Mobile) ========== */}
      <section className="py-6 md:py-10 bg-card/50 border-y border-border" data-testid="categories-carousel">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-4 md:mb-6">
            <h2 className="text-lg md:text-xl font-bold text-foreground">{t('browse_categories')}</h2>
            <Link 
              to="/categories"
              data-discover="true"
              className="text-sm text-primary font-medium flex items-center gap-1 hover:gap-2 transition-all"
            >
              {t('view_all')} <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="overflow-x-auto overflow-y-visible scrollbar-hide -mx-4 px-4 snap-x snap-mandatory scroll-p-4">
            <div className="flex gap-3 md:gap-4 py-2" style={{ width: 'max-content' }}>
              {categories.map((category) => {
                const Icon = getIconComponent(category.icon);
                return (
                  <Link
                    key={category.id}
                    to={`/marketplace?category=${category.id}`}
                    className="group flex flex-col items-center gap-2 p-4 rounded-2xl bg-card/50 backdrop-blur-sm border border-border snap-center hover:-translate-y-1.5 hover:shadow-xl hover:border-primary/40 transition-all duration-300 min-w-[100px] md:min-w-[120px]"
                    data-testid={`cat-${category.id}`}
                  >
                    <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-primary/10 dark:bg-white/10 border border-primary/20 dark:border-white/20 flex items-center justify-center group-hover:scale-110 group-hover:brightness-125 transition-all duration-300">
                      <Icon className="w-6 h-6 md:w-7 md:h-7 text-primary dark:text-white" />
                    </div>
                    <span className="text-xs md:text-sm font-medium text-center text-foreground">{category.name}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ========== SECTION 2: Latest Listings (Shows Activity/Freshness) ========== */}
      <section className="py-10 md:py-16" data-testid="latest-listings-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6 md:mb-8">
            <div>
              <span className="inline-flex items-center gap-2 text-blue-500 font-semibold text-xs md:text-sm mb-2 uppercase tracking-wider">
                <Sparkles className="w-4 h-4" />
                {t('fresh_arrivals')}
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                {t('just_listed')}
              </h2>
            </div>
            <Link 
              to="/marketplace?sort=newest" 
              className="flex items-center gap-2 text-primary font-semibold hover:gap-3 transition-all text-sm md:text-base"
            >
              {t('view_all')} <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
            </Link>
          </div>
          
          {recentListings.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="recent-listings">
              {recentListings.slice(0, 8).map((listing) => (
                <ListingCard key={listing.id} listing={listing} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-secondary/30 rounded-2xl border border-dashed border-border">
              <Sparkles className="w-12 h-12 mx-auto mb-4 text-blue-500/50" />
              <h3 className="text-lg font-semibold text-foreground mb-2">New listings coming soon!</h3>
              <p className="text-muted-foreground mb-4">Be the first to list something on the marketplace</p>
              <Link to="/create-listing" className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-full font-medium hover:bg-primary/90 transition-colors">
                Create a Listing <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}
          
          {recentListings.length > 0 && recentListings.length < 4 && (
            <div className="mt-6 text-center">
              <Link to="/create-listing" className="inline-flex items-center gap-2 text-primary font-medium hover:gap-3 transition-all">
                <span className="text-muted-foreground">Want to see your listing here?</span> Post Now <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* ========== SECTION 3: First Ad Banner (After showing value) ========== */}
      <LargePromoSection position="home_top" />

      {/* ========== SECTION 4: Featured/Promoted Listings ========== */}
      <section className="py-10 md:py-16 bg-secondary/30" data-testid="featured-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6 md:mb-8">
            <div>
              <span className="inline-flex items-center gap-2 text-amber-500 font-semibold text-xs md:text-sm mb-2 uppercase tracking-wider">
                <TrendingUp className="w-4 h-4" />
                {t('trending_now')}
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                {t('featured_listings')}
              </h2>
            </div>
            <Link 
              to="/marketplace?sort=popular" 
              className="flex items-center gap-2 text-primary font-semibold hover:gap-3 transition-all text-sm md:text-base"
            >
              {t('view_all')} <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
            </Link>
          </div>
          
          {featuredListings.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="featured-listings">
              {featuredListings.slice(0, 8).map((listing) => (
                <ListingCard key={listing.id} listing={listing} />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Promote Your Listing CTA */}
              <div className="md:col-span-2 bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-2xl p-6 md:p-8 border border-amber-500/30">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center flex-shrink-0">
                    <Star className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-foreground mb-2">{t('get_featured')}</h3>
                    <p className="text-muted-foreground mb-4">{t('promote_listing_reach')}</p>
                    <Link to="/create-listing" className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full font-medium hover:from-amber-600 hover:to-orange-600 transition-all shadow-lg shadow-amber-500/30">
                      {t('create_and_promote')} <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
              
              {/* Quick Stats */}
              <div className="bg-card rounded-2xl p-6 border border-border">
                <h4 className="font-semibold text-foreground mb-4">{t('why_feature_listing')}</h4>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-center gap-2 text-muted-foreground">
                    <Eye className="w-4 h-4 text-primary" /> {t('more_visibility')}
                  </li>
                  <li className="flex items-center gap-2 text-muted-foreground">
                    <TrendingUp className="w-4 h-4 text-green-500" /> {t('priority_search')}
                  </li>
                  <li className="flex items-center gap-2 text-muted-foreground">
                    <Star className="w-4 h-4 text-amber-500" /> {t('homepage_spotlight')}
                  </li>
                  <li className="flex items-center gap-2 text-muted-foreground">
                    <BadgeCheck className="w-4 h-4 text-blue-500" /> {t('trusted_seller_badge')}
                  </li>
                </ul>
              </div>
            </div>
          )}
          
          {featuredListings.length > 0 && featuredListings.length < 4 && (
            <div className="mt-8 text-center p-6 bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-amber-500/10 rounded-2xl border border-dashed border-amber-500/30">
              <Star className="w-8 h-8 mx-auto mb-3 text-amber-500" />
              <p className="text-foreground font-medium mb-3">{t('want_listing_stand_out')}</p>
              <Link to="/create-listing" className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full font-medium hover:from-amber-600 hover:to-orange-600 transition-all">
                {t('promote_your_listing')} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* ========== SECTION 6: Special Listings (Auction, Bulk, etc.) ========== */}
      <section className="py-10 md:py-16" data-testid="special-listings-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <span className="inline-flex items-center gap-2 text-amber-500 font-semibold text-sm mb-3 uppercase tracking-wider">
              <Gavel className="w-4 h-4" />
              {t('special_listings')}
            </span>
            <h2 className="text-2xl md:text-3xl font-bold tropical-text">
              {t('discover_deals')}
            </h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {/* Yard Sale Card */}
            <Link 
              to="/marketplace?listing_type=yard_sale"
              className="special-listing-card group h-full"
              data-testid="yard-sale-card"
            >
              <div className="p-4 md:p-6 rounded-2xl md:rounded-3xl liquid-glass bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/30 hover:border-green-500/50 transition-colors duration-200 h-full flex flex-col">
                <div className="flex items-center justify-between mb-3 md:mb-4">
                  <div className="w-10 h-10 md:w-14 md:h-14 rounded-xl md:rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center group- transition-transform">
                    <Store className="w-5 h-5 md:w-7 md:h-7 text-white" />
                  </div>
                  {specialCounts.yard_sales > 0 && (
                    <span className="px-2 md:px-3 py-0.5 md:py-1 bg-green-500 text-white text-[10px] md:text-xs font-bold rounded-full animate-pulse">
                      {specialCounts.yard_sales} LIVE
                    </span>
                  )}
                </div>
                <h3 className="text-base md:text-xl font-bold text-foreground mb-1 md:mb-2">{t('yard_sale')}</h3>
                <p className="text-muted-foreground text-xs md:text-sm mb-3 md:mb-4 flex-grow">{t('find_deals_yard_sales')}</p>
                <span className="inline-flex items-center gap-1 md:gap-2 text-green-500 font-semibold text-xs md:text-sm group-hover:gap-2 md:group-hover:gap-3 transition-all mt-auto">
                  {t('browse')} <ArrowRight className="w-3 h-3 md:w-4 md:h-4" />
                </span>
              </div>
            </Link>

            {/* Auction Card */}
            <Link 
              to="/marketplace?listing_type=auction"
              className="special-listing-card group h-full"
              data-testid="auction-card"
            >
              <div className="p-4 md:p-6 rounded-2xl md:rounded-3xl liquid-glass bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/30 hover:border-amber-500/50 transition-colors duration-200 h-full flex flex-col">
                <div className="flex items-center justify-between mb-3 md:mb-4">
                  <div className="w-10 h-10 md:w-14 md:h-14 rounded-xl md:rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center group- transition-transform">
                    <Gavel className="w-5 h-5 md:w-7 md:h-7 text-white" />
                  </div>
                  {specialCounts.auctions > 0 && (
                    <span className="px-2 md:px-3 py-0.5 md:py-1 bg-amber-500 text-white text-[10px] md:text-xs font-bold rounded-full animate-pulse">
                      {specialCounts.auctions} LIVE
                    </span>
                  )}
                </div>
                <h3 className="text-base md:text-xl font-bold text-foreground mb-1 md:mb-2">{t('auction')}</h3>
                <p className="text-muted-foreground text-xs md:text-sm mb-3 md:mb-4 flex-grow">Bid on unique items</p>
                <span className="inline-flex items-center gap-1 md:gap-2 text-amber-500 font-semibold text-xs md:text-sm group-hover:gap-2 md:group-hover:gap-3 transition-all mt-auto">
                  Auctions <ArrowRight className="w-3 h-3 md:w-4 md:h-4" />
                </span>
              </div>
            </Link>

            {/* Bulk Sale Card */}
            <Link 
              to="/marketplace?listing_type=bulk"
              className="special-listing-card group h-full"
              data-testid="bulk-sale-card"
            >
              <div className="p-4 md:p-6 rounded-2xl md:rounded-3xl liquid-glass bg-gradient-to-br from-purple-500/10 to-indigo-500/10 border border-purple-500/30 hover:border-purple-500/50 transition-colors duration-200 h-full flex flex-col">
                <div className="flex items-center justify-between mb-3 md:mb-4">
                  <div className="w-10 h-10 md:w-14 md:h-14 rounded-xl md:rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center group- transition-transform">
                    <Tag className="w-5 h-5 md:w-7 md:h-7 text-white" />
                  </div>
                  {specialCounts.bulk > 0 && (
                    <span className="px-2 md:px-3 py-0.5 md:py-1 bg-purple-500 text-white text-[10px] md:text-xs font-bold rounded-full animate-pulse">
                      {specialCounts.bulk} LIVE
                    </span>
                  )}
                </div>
                <h3 className="text-base md:text-xl font-bold text-foreground mb-1 md:mb-2">{t('bulk_sale')}</h3>
                <p className="text-muted-foreground text-xs md:text-sm mb-3 md:mb-4 flex-grow">Buy in bulk and save</p>
                <span className="inline-flex items-center gap-1 md:gap-2 text-purple-500 font-semibold text-xs md:text-sm group-hover:gap-2 md:group-hover:gap-3 transition-all mt-auto">
                  Shop Bulk <ArrowRight className="w-3 h-3 md:w-4 md:h-4" />
                </span>
              </div>
            </Link>

            {/* Giveaway Card */}
            <Link 
              to="/marketplace?listing_type=giveaway"
              className="special-listing-card group h-full"
              data-testid="giveaway-card"
            >
              <div className="p-4 md:p-6 rounded-2xl md:rounded-3xl liquid-glass bg-gradient-to-br from-pink-500/10 to-rose-500/10 border border-pink-500/30 hover:border-pink-500/50 transition-colors duration-200 h-full flex flex-col">
                <div className="flex items-center justify-between mb-3 md:mb-4">
                  <div className="w-10 h-10 md:w-14 md:h-14 rounded-xl md:rounded-2xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center group- transition-transform">
                    <Heart className="w-5 h-5 md:w-7 md:h-7 text-white" />
                  </div>
                  {specialCounts.giveaways > 0 && (
                    <span className="px-2 md:px-3 py-0.5 md:py-1 bg-pink-500 text-white text-[10px] md:text-xs font-bold rounded-full animate-pulse">
                      {specialCounts.giveaways} LIVE
                    </span>
                  )}
                </div>
                <h3 className="text-base md:text-xl font-bold text-foreground mb-1 md:mb-2">{t('giveaway')}</h3>
                <p className="text-muted-foreground text-xs md:text-sm mb-3 md:mb-4 flex-grow">Free items for you</p>
                <span className="inline-flex items-center gap-1 md:gap-2 text-pink-500 font-semibold text-xs md:text-sm group-hover:gap-2 md:group-hover:gap-3 transition-all mt-auto">
                  Get Free <ArrowRight className="w-3 h-3 md:w-4 md:h-4" />
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>


      {/* ========== SECTION 7: Homepage Middle (2-Up Side by Side Banners) ========== */}
      <DoubleBannerSection />

      {/* ========== SECTION 8: Upcoming Events Carousel ========== */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-purple-500/5 via-blue-500/5 to-cyan-500/5">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6 md:mb-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center">
                <Calendar className="w-5 h-5 md:w-6 md:h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground">{t('upcoming_events')}</h2>
                <p className="text-sm text-muted-foreground">{t('dont_miss_experiences')}</p>
              </div>
            </div>
            <Link 
              to="/events" 
              className="flex items-center gap-2 text-primary font-semibold hover:gap-3 transition-all text-sm md:text-base"
            >
              {t('browse_all_events')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          
          {upcomingEvents.length > 0 ? (
            <>
              {/* Events Carousel */}
              <div className="relative">
                <div className="flex gap-4 md:gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide -mx-4 px-4">
                  {upcomingEvents.map((event) => {
                    const eventDate = new Date(event.event_date || event.date);
                    const isToday = eventDate.toDateString() === new Date().toDateString();
                    const isTomorrow = eventDate.toDateString() === new Date(Date.now() + 86400000).toDateString();
                    
                    return (
                      <Link
                        key={event.id}
                        to={`/events/${event.slug || event.id}`}
                        className="flex-shrink-0 w-[280px] md:w-[320px] snap-start group"
                      >
                        <div className="bg-card rounded-2xl overflow-hidden border border-border hover:border-primary/50 hover:shadow-xl transition-all duration-300 h-full">
                          {/* Event Image */}
                          <div className="relative h-40 md:h-48 overflow-hidden">
                            <img
                              src={event.images?.[0] || event.image || 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=600&h=400&fit=crop'}
                              alt={event.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                            
                            {/* Date Badge */}
                            <div className="absolute top-3 left-3 bg-white dark:bg-slate-800 rounded-xl px-3 py-2 text-center shadow-lg">
                              <div className="text-xs font-bold text-primary uppercase">
                                {eventDate.toLocaleDateString('en-US', { month: 'short' })}
                              </div>
                              <div className="text-xl font-bold text-foreground">
                                {eventDate.getDate()}
                              </div>
                            </div>
                            
                            {/* Status Badge */}
                            {(isToday || isTomorrow) && (
                              <div className={`absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-bold text-white ${
                                isToday ? 'bg-red-500' : 'bg-amber-500'
                              }`}>
                                {isToday ? 'TODAY' : 'TOMORROW'}
                              </div>
                            )}
                          </div>
                          
                          {/* Event Details */}
                          <div className="p-4">
                            <h3 className="font-semibold text-foreground line-clamp-2 mb-2 group-hover:text-primary transition-colors">
                              {event.title}
                            </h3>
                            
                            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                              <Clock className="w-4 h-4" />
                              <span>{event.time || eventDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
                            </div>
                            
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <MapPin className="w-4 h-4" />
                              <span className="line-clamp-1">{event.location?.city || event.location?.venue_name || event.venue || 'Location TBA'}</span>
                            </div>
                            
                            {/* Price/Free badge */}
                            <div className="mt-3 flex items-center justify-between">
                              {event.price > 0 ? (
                                <span className="text-lg font-bold text-primary">${event.price}</span>
                              ) : (
                                <span className="px-2 py-1 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs font-semibold rounded-full">FREE</span>
                              )}
                              <span className="text-xs text-primary font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                                View Event <ArrowRight className="w-3 h-3" />
                              </span>
                            </div>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Create Event CTA */}
              <div className="bg-card rounded-2xl p-6 md:p-8 border border-border flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-foreground mb-1">{t('host_an_event')}</h3>
                  <p className="text-sm text-muted-foreground mb-3">{t('share_event_community')}</p>
                  <Link to="/events/create" className="inline-flex items-center gap-2 text-primary font-medium text-sm hover:gap-3 transition-all">
                    {t('create_event')} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
              
              {/* Browse Events */}
              <div className="bg-card rounded-2xl p-6 md:p-8 border border-border flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center flex-shrink-0">
                  <Search className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-foreground mb-1">{t('browse_all_events')}</h3>
                  <p className="text-sm text-muted-foreground mb-3">{t('check_whats_happening')}</p>
                  <Link to="/events" className="inline-flex items-center gap-2 text-primary font-medium text-sm hover:gap-3 transition-all">
                    {t('view_all')} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ========== SECTION 8.5: Quick Browse Categories ========== */}
      <section className="py-10 md:py-14">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-xl md:text-2xl font-bold text-foreground mb-2">{t('explore_marketplace')}</h2>
            <p className="text-muted-foreground">{t('find_what_looking_for')}</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link to="/marketplace?sort=newest" className="group p-4 bg-card rounded-xl border border-border hover:border-primary/50 hover:shadow-lg transition-all text-center">
              <Sparkles className="w-8 h-8 mx-auto mb-2 text-blue-500 group-hover:scale-110 transition-transform" />
              <span className="font-medium text-foreground">{t('fresh_arrivals')}</span>
            </Link>
            <Link to="/marketplace?sort=popular" className="group p-4 bg-card rounded-xl border border-border hover:border-primary/50 hover:shadow-lg transition-all text-center">
              <TrendingUp className="w-8 h-8 mx-auto mb-2 text-amber-500 group-hover:scale-110 transition-transform" />
              <span className="font-medium text-foreground">{t('popular')}</span>
            </Link>
            <Link to="/marketplace?listing_type=service" className="group p-4 bg-card rounded-xl border border-border hover:border-primary/50 hover:shadow-lg transition-all text-center">
              <Wrench className="w-8 h-8 mx-auto mb-2 text-green-500 group-hover:scale-110 transition-transform" />
              <span className="font-medium text-foreground">{t('services_nav')}</span>
            </Link>
            <Link to="/marketplace" className="group p-4 bg-card rounded-xl border border-border hover:border-primary/50 hover:shadow-lg transition-all text-center">
              <Package className="w-8 h-8 mx-auto mb-2 text-purple-500 group-hover:scale-110 transition-transform" />
              <span className="font-medium text-foreground">{t('view_all')}</span>
            </Link>
          </div>
        </div>
      </section>

      {/* ========== SECTION 8.6: Bottom Ad Banner (2-Up) ========== */}
      <DoubleBannerSection position="home_bottom" />

      {/* ========== SECTION 9: CTA Section ========== */}
      <section className="py-16 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700"></div>
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4 md:mb-6">
            {t('ready_to_start_selling')}
          </h2>
          <p className="text-white/80 text-base md:text-lg mb-8 max-w-xl mx-auto">
            {t('join_thousands_sellers')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/create-listing" 
              className="inline-flex items-center justify-center px-6 py-3 md:px-8 md:py-4 bg-white text-blue-600 rounded-full font-bold text-base md:text-lg hover:bg-blue-50 transition-colors shadow-lg"
              data-testid="cta-create-listing"
            >
              {t('create_listing')}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
            <Link 
              to="/register" 
              className="inline-flex items-center justify-center px-6 py-3 md:px-8 md:py-4 border-2 border-white/30 text-white rounded-full font-bold text-base md:text-lg hover:bg-white/10 transition-colors"
            >
              {t('register_business')}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
